import { PROD_HOSTNAME } from "@abbvie-epi/common/constants";
import { FeedbackResultsDto, SendScreeningToolFeedbackDto } from "@abbvie-epi/common/dto";
import { ScreeningTabEnum } from "@abbvie-epi/common/enums";
import { IAdobeInteraction } from "@abbvie-epi/common/models";
import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import gsap from "gsap";
import { catchError, debounceTime } from "rxjs";
import { ObjectHelpers } from "../../common/classes/ObjectHelpers";
import { IsInArrayPipe } from "../../common/pipes/is-in-array.pipe";
import { AnalyticsHelperService } from "../../services/analytics-helper.service";
import { MailService } from "../../services/mail.service";
@UntilDestroy()
@Component({
    selector: "app-feedback",
    templateUrl: "./feedback.component.html",
    styleUrls: ["./feedback.component.scss"],
    providers: [IsInArrayPipe]
})
export class FeedbackComponent implements OnInit {
    public verifiedHcp: boolean;

    public form!: ReturnType<FeedbackComponent["initFeedbackForm"]>;
    public formSubmitted: boolean = false;
    public formStep: number = 1;

    public ScreeningTabEnum = ScreeningTabEnum;
    public siteKey = "6LffwscbAAAAACJAJpZU3Q3HN9zStB674DKMv1lt"; // Real key
    public testKey = "6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI"; // test key

    public captchaKey: string = "";

    public hasSubmissionError: boolean = false;
    public isFirstFormInteraction: boolean = true;

    private readonly ANALYTICS_CONFIG_PREFIX: string = "feedback-form-";

    @Output() activeTab: EventEmitter<ScreeningTabEnum> = new EventEmitter<ScreeningTabEnum>();

    @ViewChild("progressBar", { static: false }) progressBar: ElementRef;

    // Speciality variables
    @Output() validHcp: EventEmitter<void> = new EventEmitter<void>();
    @Output() answerSubmit = new EventEmitter();

    public specialty: boolean = false;
    public specialtyOptions: string[] = [
        "MD-Internal Medicine",
        "MD-Gastroenterology",
        "MD-Family Medicine",
        "MD-Oncology",
        "MD-Other",
        "DO-Osteopathy",
        "Registered Nurse (RN)",
        "Nurse Practitioner",
        "Physician Assistant",
        "Other"
    ];

    // All getters must be defined in this component
    private analyticsGetterList: Array<keyof FeedbackComponent> = [
        "hcpSpecialty",
        "otherSpecialty",
        "howManyPatients",
        "diagnoseTreat",
        "statusAlignment",
        "nextSteps",
        "expectations",
        "recommend",
        "intuitiveEasy",
        "clearAccurate",
        "scientificContent",
        "educationalValue"
    ];

    constructor(
        public fb: FormBuilder,
        private mailService: MailService,
        private analyticsHelper: AnalyticsHelperService
    ) {}

    ngOnInit(): void {
        this.checkHcpStatus();
        this.form = this.initFeedbackForm();

        this.captchaKey = this.getCaptchaKey();

        this.initValueChangeListeners();

        this.initAnalyticsFormValueChangeListeners();
    }

    private checkHcpStatus() {
        if (sessionStorage.getItem("hcpSpecialty")) {
            this.verifiedHcp = true;
        } else {
            this.verifiedHcp = false;
        }
    }

    private initFeedbackForm() {
        const hcpSpecialty = sessionStorage.getItem("hcpSpecialty");

        return this.fb.group({
            step1: this.fb.group({
                hcpSpecialty: new FormControl<string | null>(hcpSpecialty, [Validators.required]),
                otherSpecialty: new FormControl<string | null>(null),
                howManyPatients: new FormControl<string>("", [Validators.required]),
                diagnoseTreat: new FormControl<string>("", [Validators.required])
            }),
            step2: this.fb.group({
                statusAlignment: new FormControl<string>("", [Validators.required]),
                nextSteps: new FormControl([], [Validators.required]),
                expectations: new FormControl<string>("", [Validators.required]),
                recommend: new FormControl<string>("", [Validators.required])
            }),
            step3: this.fb.group({
                intuitiveEasy: new FormControl<string>("", [Validators.required]),
                clearAccurate: new FormControl<string>("", [Validators.required]),
                scientificContent: new FormControl<string>("", [Validators.required]),
                educationalValue: new FormControl<string>("", [Validators.required])
            }),
            recaptcha: new FormControl<string | null>("", Validators.required)
        });
    }

    private initValueChangeListeners() {
        this.hcpSpecialty.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
            if (value !== "Other") {
                this.otherSpecialty.setValue(null, { emitEvent: false });
                this.otherSpecialty.clearValidators();
                this.otherSpecialty.updateValueAndValidity();
            } else {
                // Require the field when other is selected
                this.otherSpecialty.setValidators([Validators.required]);
                this.otherSpecialty.updateValueAndValidity();
            }
        });
    }

    // Speciality selection
    public isValid(valid: boolean) {
        if (valid) {
            this.validHcp.emit();
        }
    }

    public getSelectedAnswer(selection: string) {
        this.hcpSpecialty.setValue(selection, { emitEvent: true });
    }

    public submitSpecialty() {
        const specialty = this.hcpSpecialty.value;
        const otherSpecialty = this.otherSpecialty.value;

        if ((specialty && specialty !== "Other") || (specialty === "Other" && otherSpecialty)) {
            if (specialty === "Other") {
                sessionStorage.setItem("hcpSpecialty", otherSpecialty);
            } else if (specialty && specialty !== "Other") {
                sessionStorage.setItem("hcpSpecialty", specialty);
            }

            this.isValid(true);
            this.verifiedHcp = true;
        }
    }

    // Form button functions
    public continue() {
        if (!this.verifiedHcp) {
            this.submitSpecialty();
        }

        this.trackStepChange(this.formStep);

        this.formStep++;
        gsap.to(this.progressBar.nativeElement, { width: (100 * this.formStep) / 3 + "%" });
        window.scroll(0, 0);
    }

    public submitForm() {
        this.hasSubmissionError = false;

        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.form.updateValueAndValidity();
            return;
        }

        // Run email API call here

        const formValues = this.form.value;

        const dto: SendScreeningToolFeedbackDto = {
            data: {
                hcpSpecialty: formValues.step1?.otherSpecialty || formValues.step1?.hcpSpecialty || "",
                patientCount: formValues.step1?.howManyPatients || "",
                diagnosisOrTreatmentMethods: formValues.step1?.diagnoseTreat || "",
                existingStatusToolAlignment: formValues.step2?.statusAlignment || "",
                patientNextSteps: formValues.step2?.nextSteps || [],
                toolVsExpectations: formValues.step2?.expectations || "",
                recommend: formValues.step2?.recommend || "",
                intuitiveSiteNavigation: formValues.step3?.intuitiveEasy || "",
                clearAccurateInformation: formValues.step3?.clearAccurate || "",
                scientificContent: formValues.step3?.scientificContent || "",
                educationalValue: formValues.step3?.educationalValue || ""
            }
        };

        this.mailService
            .sendResultsEmail(dto)
            .pipe(
                catchError((err) => {
                    console.error(err);
                    this.trackSubmissionError(err);
                    this.hasSubmissionError = true;
                    throw err;
                })
            )
            .subscribe(() => {
                // If successful:
                this.trackSubmissionSuccess(dto.data);
                this.formSubmitted = true;
                document.body.scrollTop = document.documentElement.scrollTop = 0;
                this.form.reset();
            });
    }

    public changeTab(activeTab: ScreeningTabEnum) {
        if (activeTab) {
            this.activeTab.emit(activeTab);
        }
    }

    // handle new tab changes
    public handleTabChange(newActiveTab: ScreeningTabEnum) {
        this.changeTab(newActiveTab);
    }

    // Captcha v2
    resolved(captchaResponse: string) {
        this.recaptcha.patchValue(captchaResponse);
    }

    // Updating form data
    public updateNextSteps(event: any) {
        const stepName = event.srcElement.value;
        const isChecked = event.target.checked;

        let existingArray = [...this.nextSteps.value];
        let updatedArray;

        if (isChecked) {
            // include in form array
            if (!existingArray.includes(stepName)) {
                // Checking for none of the above
                if (stepName === "none of the above") {
                    existingArray = [];
                } else if (existingArray.includes("none of the above")) {
                    const noneOfTheAboveIndex = existingArray.findIndex((item: string) => item === "none of the above");
                    existingArray.splice(noneOfTheAboveIndex, 1);
                }
                existingArray.push(stepName);
                updatedArray = existingArray.slice();
            }
        } else {
            // remove from form array if it exists
            const existingIndex = existingArray.findIndex((item: string) => item === stepName);

            if (existingIndex >= 0) {
                updatedArray = existingArray.slice(0, existingIndex).concat(existingArray.slice(existingIndex + 1));
            }
        }

        this.nextSteps.setValue(updatedArray || existingArray);
    }

    private getCaptchaKey(): string {
        const isProd = window.location.hostname.endsWith(PROD_HOSTNAME);

        return isProd ? this.siteKey : this.testKey;
    }

    private get recaptcha(): FormControl {
        return this.form.get("recaptcha") as FormControl;
    }

    private initAnalyticsFormValueChangeListeners(): void {
        this.analyticsGetterList.forEach((getterMethodName) => {
            const control = this[getterMethodName as keyof FeedbackComponent] as FormControl;

            control.valueChanges.pipe(untilDestroyed(this), debounceTime(500)).subscribe(async (value) => {
                if (value) {
                    await this.trackFormFieldUpdates(this.ANALYTICS_CONFIG_PREFIX + getterMethodName, value);
                }
            });
        });
    }

    private async trackFormFieldUpdates(analyticsId: string, value: any): Promise<void> {
        value = value ? String(value) : "";

        const baseAnalytics = this.getBaseFormAnalytics();

        const idSpecificAnalytics = (await this.analyticsHelper.getInteractionById(analyticsId)) ?? {};

        const finalAnalytics = ObjectHelpers.mergeObjects(baseAnalytics, idSpecificAnalytics);

        finalAnalytics.link = finalAnalytics.link ?? {};
        finalAnalytics.link.name = `${finalAnalytics.link.name ? finalAnalytics.link.name + " " : ""}${value}`;
        finalAnalytics.link.title = value;

        finalAnalytics.form = finalAnalytics.form ?? {};

        if (this.isFirstFormInteraction) {
            finalAnalytics.link.type = "form-initiation";
            finalAnalytics.form.stepName = "initiation";

            this.isFirstFormInteraction = false;
        }

        finalAnalytics.form.fieldValue = value;

        this.analyticsHelper.trackCustom(finalAnalytics);
    }

    private trackStepChange(stepNumber: number): void {
        const baseAnalytics = this.getBaseFormAnalytics();

        baseAnalytics.link = baseAnalytics.link ?? {};
        baseAnalytics.form = baseAnalytics.form ?? {};

        baseAnalytics.link = {
            ...baseAnalytics.link,
            name: `STEP ${stepNumber}: Continue`,
            title: "Continue"
        };

        baseAnalytics.form = {
            ...baseAnalytics.form,
            fieldName: `STEP ${stepNumber}`,
            fieldValue: "Continue"
        };

        this.analyticsHelper.trackCustom(baseAnalytics);
    }

    private trackSubmissionError(error: Error): void {
        const baseAnalytics = this.getBaseFormAnalytics();

        const analyticsOverrides: IAdobeInteraction = {
            link: {
                name: "Your feedback submit",
                title: "Send",
                type: "form-submission"
            },
            form: {
                errors: error.message
            }
        };

        const finalAnalytics = ObjectHelpers.mergeObjects(baseAnalytics, analyticsOverrides);

        this.analyticsHelper.trackCustom(finalAnalytics);
    }

    private async trackSubmissionSuccess(data: FeedbackResultsDto): Promise<void> {
        const trackingFieldValues: Record<string, any> = {
            "HCP Specialty": data.hcpSpecialty,
            "How many patients with CP do you see in clinic in a month?": data.patientCount,
            "Do you currently diagnose and/or treat EPI?": data.diagnosisOrTreatmentMethods,
            "If you know the EPI status of your patient, does it align with the Screen for EPI in CP prediction?":
                data.existingStatusToolAlignment,
            'For a "more likely has EPI" prediction what is your next step for your patient?': data.patientNextSteps,
            "Did the Screen for EPI in CP tool meet your expectations?": data.toolVsExpectations,
            "Would you recommend the use of the Screen for EPI in CP tool to other clinicians?": data.recommend,
            "Did you find this website to be intuitive and easy to navigate?": data.intuitiveSiteNavigation,
            "Is the information presented in this website clear and accurate?": data.clearAccurateInformation,
            "In terms of scientific content, was there…": data.scientificContent,
            "How would you rate the educational value of this website?": data.educationalValue
        };

        const analytics: IAdobeInteraction =
            (await this.analyticsHelper.getInteractionById("feedback-form-submit-success")) || {};

        if (!analytics) {
            return;
        }

        analytics.form = analytics.form ?? {};

        analytics.form.fieldValues = this.formatFields(trackingFieldValues);

        this.analyticsHelper.trackCustom(analytics);
    }

    private formatFields(obj: Record<string, any>) {
        return Object.entries(obj)
            .map(([key, value]) => {
                return `${key}:${value}`;
            })
            .join("|");
    }

    private getBaseFormAnalytics(): IAdobeInteraction {
        return {
            link: {
                type: "form-interaction"
            },
            component: {
                name: "form",
                title: "Your feedback",
                position: "content",
                type: "form"
            },
            content: {
                type: "form"
            },
            interaction: {
                type: "form-interaction"
            },
            form: {
                name: "Your feedback",
                category: "interaction",
                subCategory: "more info",
                stepName: "interaction",
                stepNumber: `${this.formStep}`
            }
        };
    }

    // getters
    public get step1(): FormGroup {
        return this.form.get("step1") as FormGroup;
    }

    public get hcpSpecialty(): FormControl {
        return this.step1.get("hcpSpecialty") as FormControl;
    }

    public get otherSpecialty(): FormControl {
        return this.step1.get("otherSpecialty") as FormControl;
    }

    public get step2(): FormGroup {
        return this.form.get("step2") as FormGroup;
    }

    public get step3(): FormGroup {
        return this.form.get("step3") as FormGroup;
    }

    public get nextSteps(): FormControl {
        return this.step2.get("nextSteps") as FormControl;
    }

    public get nextStepsAsArray(): string[] {
        return [...this.nextSteps.value];
    }

    public get currentStep(): FormGroup {
        return this.form.get("step" + this.formStep) as FormGroup;
    }

    // Private getters used by analytics
    public get howManyPatients(): FormControl {
        return this.step1.get("howManyPatients") as FormControl;
    }

    public get diagnoseTreat(): FormControl {
        return this.step1.get("diagnoseTreat") as FormControl;
    }

    public get statusAlignment(): FormControl {
        return this.step2.get("statusAlignment") as FormControl;
    }
    public get expectations(): FormControl {
        return this.step2.get("expectations") as FormControl;
    }
    public get recommend(): FormControl {
        return this.step2.get("recommend") as FormControl;
    }

    public get intuitiveEasy(): FormControl {
        return this.step3.get("intuitiveEasy") as FormControl;
    }
    public get clearAccurate(): FormControl {
        return this.step3.get("clearAccurate") as FormControl;
    }
    public get scientificContent(): FormControl {
        return this.step3.get("scientificContent") as FormControl;
    }
    public get educationalValue(): FormControl {
        return this.step3.get("educationalValue") as FormControl;
    }
}
